import { Banknote, Receipt } from "lucide-react";
import { useEffect, useState } from "react";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";

const mxn = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" });

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="border rounded-2xl bg-white p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon className="h-4 w-4" strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-widest">{label}</span>
      </div>
      <p className="text-2xl font-bold font-heading">{value}</p>
    </div>
  );
}

function DoctorsTable({ doctors }) {
  return (
    <div className="border rounded-2xl bg-white overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Profesional</TableHead>
            <TableHead className="text-right">Consultas</TableHead>
            <TableHead className="text-right">Ingresos</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {doctors.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-8">
                Aún no hay pagos verificados.
              </TableCell>
            </TableRow>
          )}
          {doctors.map((d) => (
            <TableRow key={d.professional_id} data-testid={`revenue-doctor-${d.professional_id}`}>
              <TableCell className="font-semibold">{d.name}</TableCell>
              <TableCell className="text-right">{d.count}</TableCell>
              <TableCell className="text-right text-primary font-semibold">{mxn.format(d.revenue)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function MonthsTable({ months }) {
  return (
    <div className="border rounded-2xl bg-white overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Mes</TableHead>
            <TableHead className="text-right">Consultas</TableHead>
            <TableHead className="text-right">Ingresos</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {months.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-8">
                Aún no hay pagos verificados.
              </TableCell>
            </TableRow>
          )}
          {months.map((m) => (
            <TableRow key={m.month} data-testid={`revenue-month-${m.month}`}>
              <TableCell className="font-semibold capitalize">{m.label}</TableCell>
              <TableCell className="text-right">{m.count}</TableCell>
              <TableCell className="text-right text-primary font-semibold">{mxn.format(m.revenue)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function RecentTable({ recent }) {
  return (
    <div className="border rounded-2xl bg-white overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Fecha</TableHead>
            <TableHead>Paciente</TableHead>
            <TableHead>Folio</TableHead>
            <TableHead>Profesional</TableHead>
            <TableHead className="text-right">Monto</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {recent.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">
                Aún no hay pagos verificados.
              </TableCell>
            </TableRow>
          )}
          {recent.map((t, idx) => (
            <TableRow key={`${t.date}-${idx}`}>
              <TableCell className="text-muted-foreground">
                {t.date ? new Date(t.date).toLocaleDateString("es-MX") : "—"}
              </TableCell>
              <TableCell className="font-semibold">{t.patient}</TableCell>
              <TableCell>{t.folio}</TableCell>
              <TableCell className="text-muted-foreground">{t.professional}</TableCell>
              <TableCell className="text-right text-primary font-semibold">{mxn.format(t.amount)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function RevenueTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/revenue").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <p className="text-sm text-muted-foreground" data-testid="revenue-loading">Cargando ingresos…</p>;
  }

  const { totals, by_month, by_doctor, recent, price_ref } = data;
  return (
    <div className="space-y-10" data-testid="revenue-tab">
      <p className="text-sm text-muted-foreground" data-testid="revenue-note">
        Los pacientes pagan directo en la liga de Stripe de cada especialista y el profesional
        verifica el comprobante. Cifras estimadas con el precio de consulta de {mxn.format(price_ref)}.
      </p>
      <div className="grid grid-cols-2 gap-4 max-w-xl">
        <StatCard icon={Banknote} label="Ingresos (estimado)" value={mxn.format(totals.revenue)} testId="revenue-total" />
        <StatCard icon={Receipt} label="Pagos verificados" value={totals.transactions} testId="revenue-count" />
      </div>
      <div>
        <h3 className="font-heading font-semibold mb-3">Por profesional</h3>
        <DoctorsTable doctors={by_doctor} />
      </div>
      <div>
        <h3 className="font-heading font-semibold mb-3">Por mes</h3>
        <MonthsTable months={by_month} />
      </div>
      <div>
        <h3 className="font-heading font-semibold mb-3">Pagos verificados recientes</h3>
        <RecentTable recent={recent} />
      </div>
    </div>
  );
}
